import { TemplateType } from '@/types/boot';
import { TemplateConfigurations, type TemplateFeatures } from '@/constants/templateConfig';
import type { PaymentMethod } from '@/types/checkout';
import type { PaymentMethodEnum } from '@/types/common';
import { OnePaymentView } from '@/templateViews/OnePaymentView';
import {
  filterByConfiguredAvailablePaymentMethods,
  getConfiguredPaymentMethodEnums,
} from '@/utils/paymentMethodFilters';
import {
  CheckoutTemplate,
  type PaymentMethodSelectionContext,
  type TemplateInitContext,
  type TemplateView,
} from './CheckoutTemplate';

/**
 * Single-payment-method checkout: the publisher configures exactly one
 * method via `checkoutConfiguration.availablePaymentMethods` (on the boot
 * JWT — see source-of-truth note below) and it is the only option the
 * user sees.
 *
 * Always eligible — failure to actually deliver the configured method is
 * a hard error surfaced by {@link validatePreRender}, not a silent
 * fallback to DEFAULT. (Falling back would hide a publisher configuration
 * bug behind a confusing "wrong template" experience.)
 *
 * Source-of-truth note (validated 2026-05-21 against staging BE):
 * `availablePaymentMethods` is echoed on `bootData.checkoutConfiguration`
 * (boot JWT, SINGULAR), NOT on `checkoutData.checkoutConfigurations`
 * (`/checkout/v3`, plural — that field is never populated). The previous
 * implementation read the plural form, which always resolved to `[]` and
 * caused `validatePreRender` to be effectively dead (always erroring,
 * silently swallowed because the BE pre-filters `paymentMethods` itself).
 */
export class SinglePaymentTemplate extends CheckoutTemplate {
  readonly type: TemplateType = TemplateType.SINGLE_PAYMENT_METHOD;
  readonly View: TemplateView = OnePaymentView;
  readonly features: TemplateFeatures = TemplateConfigurations[TemplateType.SINGLE_PAYMENT_METHOD];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isEligible(_ctx: TemplateInitContext): boolean {
    return true;
  }

  override validatePreRender({ bootData, paymentMethods }: TemplateInitContext): string | null {
    const allowed = getConfiguredPaymentMethodEnums(bootData?.checkoutConfiguration);
    if (allowed.length === 0) return 'paymentMethodNotAvailable';
    if (!this.hasRequiredMethod(allowed, paymentMethods ?? [])) return 'paymentMethodNotAvailable';
    return null;
  }

  /**
   * BE returns exactly one payment method for SINGLE_PAYMENT_METHOD (the
   * one configured via boot's `availablePaymentMethods`, after
   * {@link filterAvailablePaymentMethods} runs). The default IS that
   * method — including express ones like Apple/Google Pay, which
   * `pickFirstNonExpressPaymentMethod` would (misleadingly) try to skip.
   */
  resolveDefaultPaymentMethod({ paymentMethods }: PaymentMethodSelectionContext) {
    return paymentMethods[0]?.paymentMethodName as PaymentMethodEnum | undefined;
  }

  override filterAvailablePaymentMethods(
    paymentMethods: PaymentMethod[],
    { bootData }: TemplateInitContext
  ): PaymentMethod[] {
    return filterByConfiguredAvailablePaymentMethods(paymentMethods, bootData?.checkoutConfiguration);
  }

  private hasRequiredMethod(allowed: PaymentMethodEnum[], paymentMethods: PaymentMethod[]): boolean {
    const required = allowed[0];
    return paymentMethods.some((pm) => pm.paymentMethodName === required);
  }
}
